import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';

import { Redraft } from '@/components/Redraft';
import { useCurrent } from '@/hooks/useCurrent';
import { Emoji } from '@/imports/ui/chakra/components/Emoji';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { useImpersonation } from '@/modules/accounts/impersonation';

import {
  PrivacyPolicyModal_AcceptPrivacyPolicy,
  PrivacyPolicyModal_AcceptPrivacyPolicyVariables,
} from './graphql-types/PrivacyPolicyModal_AcceptPrivacyPolicy';

const ACCEPT_PRIVACY_POLICY = gql`
  mutation PrivacyPolicyModal_AcceptPrivacyPolicy(
    $input: AcceptPrivacyPolicyInput!
  ) {
    acceptPrivacyPolicy(input: $input) {
      user {
        _id
        acceptedPrivacyPolicy
      }
    }
  }
`;

export const PrivacyPolicyModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = (props) => {
  const current = useCurrent();
  const { impersonationActive } = useImpersonation();
  const [checked, setChecked] = React.useState(
    current.user?.acceptedPrivacyPolicy ? true : false
  );

  const [acceptPrivacyPolicy, acceptPrivacyPolicyResult] = useMutation<
  PrivacyPolicyModal_AcceptPrivacyPolicy,
  PrivacyPolicyModal_AcceptPrivacyPolicyVariables
  >(ACCEPT_PRIVACY_POLICY);

  const showClose =
    !current.tenant?.obnoxiousPrivacyPolicy ||
    !!current.user?.acceptedPrivacyPolicy;

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      scrollBehavior="inside"
      closeOnOverlayClick={showClose}
      /** Backdoor for admins to impersonate users without accepting the pp for them */
      closeOnEsc={impersonationActive || showClose}
      isCentered
      size={['full', 'full', 'xl']}
    >
      <ModalOverlay />
      <ModalContent>
        {showClose && <ModalCloseButton color="black" />}
        <ModalHeader textAlign="center" pt="10" pb="6">
          <VStack spacing="2">
            <Emoji symbol="📃" fontSize="4xl" />
            <Heading size="md">
              {!current.user?.acceptedPrivacyPolicy && (
                <>{`Please Read and Accept the `}</>
              )}
              {`${current.tenant?.displayName} Privacy Policy`}
            </Heading>
          </VStack>
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" pb="6">
          <Box
            bg="gray.100"
            p="4"
            h="xs"
            overflow="auto"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
          >
            {current.tenant?.privacyPolicy && (
              <Prose>
                <Redraft rawDraftContentState={current.tenant?.privacyPolicy} />
              </Prose>
            )}
          </Box>
        </ModalBody>
        <ModalFooter pt="2" pb="6">
          {!current.user?.acceptedPrivacyPolicy ? (
            <Flex w="full" justify="space-between">
              <Checkbox
                isDisabled={!!current.user?.acceptedPrivacyPolicy}
                isChecked={checked}
                onChange={() => setChecked((oldValue) => !oldValue)}
                mr="2"
              >
                {`I've read and accepted the privacy policy`}
              </Checkbox>
              <Button
                isDisabled={!checked || !!current.user?.acceptedPrivacyPolicy}
                isLoading={acceptPrivacyPolicyResult.loading}
                onClick={async () => {
                  if (checked) {
                    await acceptPrivacyPolicy({
                      variables: { input: { isPartOfOnboardingFlow: false } },
                    });
                    props.onClose();
                  }
                }}
              >
                Accept
              </Button>
            </Flex>
          ) : (
            <Button onClick={props.onClose}>Okay</Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
